const SITE_URL = "https://meuambiente.guararapes.com.br";
const SITE_NAME = "Meu Ambiente Guararapes";
const BRAND = "Guararapes";

const DESCRIPTION_LIMIT = 155;
const SHORT_ABOUT_LIMIT = 70;
const APPROVED_STATUS = 1;

const IMAGE_CDN_PATH = "/.netlify/images";
const STATIC_SITEMAP_PATHS = ["", "ambientes", "profissionais", "termos-de-utilizacao"];

const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

const cleanText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (text, limit = DESCRIPTION_LIMIT) => {
  if (text.length <= limit) return text;

  const slice = text.slice(0, limit - 1);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;

  return `${cut.replace(/[\s.,;:!?-]+$/, "")}…`;
};

const asSentence = (text) => (/[.!?…]$/.test(text) ? text : `${text}.`);

const canonicalUrl = (path) => {
  const trimmed = String(path || "").replace(/^\/+|\/+$/g, "");

  return trimmed ? `${SITE_URL}/${trimmed}/` : `${SITE_URL}/`;
};

const profileUrl = (id) => canonicalUrl(`perfil/${id}`);
const projectUrl = (id) => canonicalUrl(`ambiente/${id}`);

const isRemoteUrl = (value) => /^https?:\/\/\S+$/.test(String(value || "").trim());

// O WhatsApp descarta imagens de preview grandes; o redimensionador do Netlify
// entrega a foto original recortada no tamanho do Open Graph.
const ogImageUrl = (source) =>
  `${SITE_URL}${IMAGE_CDN_PATH}?url=${encodeURIComponent(
    source
  )}&w=${OG_IMAGE_WIDTH}&h=${OG_IMAGE_HEIGHT}&fit=cover&fm=jpg&q=75`;

const DEFAULT_IMAGE = ogImageUrl("/images/01.png");

const isResizedImage = (url) => Boolean(url) && url.includes(IMAGE_CDN_PATH);

const ogImage = (...sources) => {
  const source = sources.find(isRemoteUrl);

  return source ? ogImageUrl(source.trim()) : DEFAULT_IMAGE;
};

const formatLocation = (city, uf) =>
  [cleanText(city), cleanText(uf).toUpperCase()].filter(Boolean).join("/");

const galleryImages = (gallery) => {
  try {
    const items = typeof gallery === "string" ? JSON.parse(gallery) : gallery;

    return (Array.isArray(items) ? items : [])
      .map((item) => item && item.url)
      .filter(isRemoteUrl);
  } catch (error) {
    return [];
  }
};

const isoDate = (value) => {
  if (!value) return undefined;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
};

const isApproved = (project) => Number(project.status) === APPROVED_STATUS;

const getIndexableProfileIds = (projects) =>
  new Set(projects.filter(isApproved).map((project) => project.user_id));

const buildProfileSeo = (profile, indexableProfileIds) => {
  const name = cleanText(profile.company_name);
  const about = cleanText(profile.company_about);
  const location = formatLocation(profile.city, profile.uf);
  const fallbackName = ["Profissional", location].filter(Boolean).join(" em ");

  const description =
    about.length >= SHORT_ABOUT_LIMIT
      ? about
      : [
          asSentence(name ? [name, location].filter(Boolean).join(", ") : fallbackName),
          about && asSentence(about),
          `Veja os ambientes no ${SITE_NAME}.`,
        ]
          .filter(Boolean)
          .join(" ");

  return {
    title: `${name || fallbackName} | ${BRAND}`,
    description: truncate(description),
    canonical: profileUrl(profile.id),
    noindex: !indexableProfileIds.has(profile.id),
    image: ogImage(profile.background, profile.company_avatar),
    type: "profile",
  };
};

const buildProjectSeo = (project, owner) => {
  const name = cleanText(project.name);
  const about = cleanText(project.about);
  const ownerName = cleanText(owner && owner.company_name);
  const location = formatLocation(project.city, project.uf);
  const typeAndPlace = [cleanText(project.project_type), location]
    .filter(Boolean)
    .join(" em ");

  const description =
    about ||
    `${[name, typeAndPlace, ownerName && `por ${ownerName}`]
      .filter(Boolean)
      .join(", ")}. Veja no ${SITE_NAME}.`;

  return {
    title: [ownerName, name, BRAND].filter(Boolean).join(" | "),
    description: truncate(description),
    canonical: projectUrl(project.id),
    noindex: !isApproved(project),
    image: ogImage(galleryImages(project.gallery)[0]),
    type: "article",
  };
};

module.exports = {
  SITE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  STATIC_SITEMAP_PATHS,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  cleanText,
  canonicalUrl,
  profileUrl,
  projectUrl,
  isRemoteUrl,
  isResizedImage,
  galleryImages,
  isoDate,
  getIndexableProfileIds,
  buildProfileSeo,
  buildProjectSeo,
};
